import React from 'react'
// @src imports
import { CatalogRibbon } from 'src/catalog/components'
import { useCollection, useGetSendableCards } from 'src/react_query'
// relative imports
import styles from './collection.module.scss'
import CollectionHeader from '../Headers/CollectionHeader'
import { useActions } from 'src/hooks'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { DefaultError, Transition } from 'src/chrome'
import {
  useFavoriteSendableCard,
  useUnfavoriteSendableCard,
} from 'src/react_query'

interface CollectionProps {
  collectionId: string
}

const Collection: React.FC<CollectionProps> = props => {
  const { collectionId } = props
  const favoriteCard = useFavoriteSendableCard()
  const unfavoriteCard = useUnfavoriteSendableCard()
  const actions = useActions()

  const { data: collectionData } = useCollection(
    { id: collectionId },
    { suspense: true },
  )
  const collection = collectionData?.collection! // suspense, assume data exists

  const collectionCardsQuery = useGetSendableCards({
    search: '',
    category: undefined,
    collection: collectionId,
    isPhotoDrop: undefined,
    favorite: undefined,
  })

  const collectionCards = collectionCardsQuery.data?.pages?.flat() ?? []
  const {
    hasNextPage: hasMoreCollectionCards,
    fetchNextPage: loadMoreCollectionCards,
    isFetchingNextPage: isLoadingMoreCollectionCards,
  } = collectionCardsQuery

  const handleFavoriteCard = async (id: string, isFavorite: boolean) => {
    try {
      if (isFavorite) {
        await unfavoriteCard.mutateAsync({ id })
      } else {
        await favoriteCard.mutateAsync({ id })
      }
    } catch (error) {
      console.error('Failed to favorite/unfavorite card.')
    }
  }

  const handleOnClickCard = (id: string, typename?: string | undefined) => {
    if (typename !== 'SendableCard') {
      return
    }
    actions.selectedCatalogCard(typename, id)
  }

  const handleClose = () => actions.dismissedCatalogSubroute()

  return (
    <div className={styles.collectionContainer}>
      <CollectionHeader
        title={collection.title}
        textColor={collection.textColor}
        backgroundStartColor={collection.backgroundStartColor}
        backgroundEndColor={collection.backgroundEndColor}
        card={collection.cards[0]}
        handleFavoriteCard={handleFavoriteCard}
        onClose={handleClose}
      />
      <div className={styles.cardGrid}>
        <CatalogRibbon
          type={`collection_${collection.id}_card`}
          items={collectionCards}
          isWrapContentEnabled={true}
          onClick={handleOnClickCard}
          onClose={handleClose}
          canFavorite={true}
          handleFavoriteCard={handleFavoriteCard}
          isLoadingMore={isLoadingMoreCollectionCards}
          hasMore={hasMoreCollectionCards}
          loadMore={
            hasMoreCollectionCards ? loadMoreCollectionCards : undefined
          }
        />
      </div>
    </div>
  )
}

export default suspenseBoundary({
  component: Collection,
  unresolved: <Transition />,
  failure: DefaultError,
})
